import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  Users, Megaphone, IndianRupee, TicketCheck, ShieldCheck, Download,
  Send, Search, Plus, Loader2, BadgeCheck, ExternalLink, Boxes, Activity, TrendingUp, BookOpenCheck, Sparkles, Upload, Bike,
} from 'lucide-react';
import AdminInventory from '../components/AdminInventory';
import AdminOpsConsole from '../components/AdminOpsConsole';
import AdminAdvanced from '../components/AdminAdvanced';
import AdminPriceWatch from '../components/AdminPriceWatch';
import AdminLedger from '../components/AdminLedger';
import AdminAnalytics from '../components/AdminAnalytics';
import AdminComplianceHeatmap from '../components/AdminComplianceHeatmap';
import AdminLowPerformance from '../components/AdminLowPerformance';
import AdminFleetPanel from '../components/AdminFleetPanel';
import { PrintShell, PrintButton } from '../components/PrintShell';
import { AdminTutorialSetting } from '../components/MerchantTutorialVideo';
import CompanyProfileTab from '../components/CompanyProfileTab';
import CustomerInsights from '../components/CustomerInsights';
import { AdminTaxStatements } from '../components/TaxStatements';
import AdminScorecards from '../components/AdminScorecards';
import AdminApprovals from '../components/AdminApprovals';

const TABS = [
  { id: 'ops', label: 'Operations Console', Icon: Activity },
  { id: 'approvals', label: 'Partner & listing approvals', Icon: BadgeCheck },
  { id: 'customers', label: 'Customers', Icon: Users },
  { id: 'insights', label: 'Top customers', Icon: Users },
  { id: 'inventory', label: 'Inventory & Codes', Icon: Boxes },
  { id: 'catalog', label: 'Master Catalog', Icon: Sparkles },
  { id: 'ledger', label: 'Order Ledger', Icon: BookOpenCheck },
  { id: 'analytics', label: 'Analytics & Visitors', Icon: TrendingUp },
  { id: 'compliance', label: 'Compliance Heatmap', Icon: ShieldCheck },
  { id: 'reputation', label: 'Low Performance', Icon: TrendingUp },
  { id: 'scorecards', label: 'Seller Scorecards', Icon: BadgeCheck },
  { id: 'fleet',      label: 'Delivery Fleet',  Icon: Bike },
  { id: 'price-watch', label: 'Price Watch', Icon: TrendingUp },
  { id: 'advanced', label: 'LMS / QMS / DPS', Icon: BadgeCheck },
  { id: 'broadcasts', label: 'Broadcasts', Icon: Megaphone },
  { id: 'financials', label: 'Financials', Icon: IndianRupee },
  { id: 'company', label: 'Company & Tax IDs', Icon: BadgeCheck },
  { id: 'tax', label: 'TDS / TCS register', Icon: IndianRupee },
  { id: 'kyc', label: 'KYC verify', Icon: ShieldCheck },
  { id: 'tickets', label: 'Support tickets', Icon: TicketCheck },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('ops');

  if (loading) return <div className="py-12 text-center text-ink/60">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace/>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#0D47A1] text-white grid place-items-center"><ShieldCheck className="w-6 h-6"/></div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0D47A1]">Admin · Business control</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl">BharatCart Admin Panel</h1>
          <p className="text-xs text-ink/60 mt-1">Customer database · Broadcasts · Financial statements · KYC verification · Support tickets</p>
        </div>
      </div>

      <AdminTutorialSetting/>

      <div className="mt-6 flex gap-2 flex-wrap border-b border-border pb-1">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-sm font-semibold border-b-2 transition ${
              tab === id ? 'border-[#0D47A1] text-[#0D47A1] bg-[#E3F2FD]' : 'border-transparent text-ink/60 hover:text-[#0D47A1]'
            }`}>
            <Icon className="w-4 h-4"/>{label}
          </button>
        ))}
      </div>

      {tab === 'ops' && <AdminOpsConsole/>}
      {tab === 'customers' && <CustomersTab/>}
      {tab === 'inventory' && <AdminInventory/>}
      {tab === 'ledger' && <AdminLedger/>}
      {tab === 'analytics' && <div className="mt-6"><AdminAnalytics/></div>}
      {tab === 'compliance' && <div className="mt-6"><AdminComplianceHeatmap/></div>}
      {tab === 'price-watch' && <AdminPriceWatch/>}
      {tab === 'advanced' && <AdminAdvanced/>}
      {tab === 'broadcasts' && <BroadcastsTab/>}
      {tab === 'financials' && <FinancialsTab/>}
      {tab === 'company' && <CompanyProfileTab/>}
      {tab === 'insights' && <CustomerInsights/>}
      {tab === 'scorecards' && <AdminScorecards/>}
      {tab === 'approvals' && <AdminApprovals/>}
      {tab === 'tax' && <AdminTaxStatements/>}
      {tab === 'catalog' && <AdminCatalogTab/>}
      {tab === 'kyc' && <KycVerifyTab/>}
      {tab === 'tickets' && <TicketsTab/>}
    </div>
  );
}

// ============ Customers ============
function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [village, setVillage] = useState('');

  const reload = async () => {
    const params = {};
    if (q) params.q = q;
    if (village) params.village = village;
    const r = await api.get('/admin/customers', { params });
    setRows(r.data);
  };
  useEffect(() => { reload().catch(() => {}); }, []);

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/export.csv?token=download`;
    window.open(url, '_blank');
    toast.info('Downloading CSV…');
  };

  return (
    <div className="mt-5 space-y-4">
      <div className="card-soft p-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"/>
          <input data-testid="cust-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Name / phone / email" className="ainp pl-9"/>
        </div>
        <input data-testid="cust-village" value={village} onChange={e=>setVillage(e.target.value)} placeholder="Village" className="ainp max-w-[160px]"/>
        <button data-testid="cust-search-btn" onClick={reload} className="rounded-full bg-[#0D47A1] hover:bg-[#0a3578] text-white px-4 py-2 text-sm font-semibold">Search</button>
        <button data-testid="cust-export-csv" onClick={exportCsv} className="rounded-full border border-border bg-white hover:border-[#0D47A1] hover:text-[#0D47A1] px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5"><Download className="w-4 h-4"/>Export CSV</button>
        <span className="ml-auto text-sm text-ink/60">{rows.length} customers</span>
      </div>

      <div className="card-soft p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-ink/70 text-xs uppercase tracking-wider">
            <tr><th className="px-3 py-2 text-left">Member ID</th><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Phone</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Village</th><th className="px-3 py-2 text-right">Orders</th><th className="px-3 py-2 text-right">Lifetime ₹</th><th className="px-3 py-2 text-left">Last order</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`cust-row-${r.id}`} className="border-t border-border hover:bg-[#FFF8EE]">
                <td className="px-3 py-2 font-mono text-xs font-semibold text-[#1B5E20]">{r.serial_id || '—'}</td>
                <td className="px-3 py-2 font-semibold">{r.name || '(unnamed)'}</td>
                <td className="px-3 py-2">{r.phone} {r.alt_phone && <span className="text-ink/40 text-xs">· {r.alt_phone}</span>}</td>
                <td className="px-3 py-2">{r.email || <span className="text-ink/30">—</span>}</td>
                <td className="px-3 py-2">{r.address?.village || <span className="text-ink/30">—</span>}</td>
                <td className="px-3 py-2 text-right">{r.order_count}</td>
                <td className="px-3 py-2 text-right font-bold">₹{r.lifetime_inr.toLocaleString('en-IN', {maximumFractionDigits: 0})}</td>
                <td className="px-3 py-2 text-xs text-ink/60">{r.last_order_at ? new Date(r.last_order_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-ink/40">No customers match.</td></tr>}
          </tbody>
        </table>
      </div>
      <style>{`.ainp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;outline:none}.ainp:focus{border-color:#0D47A1;box-shadow:0 0 0 3px rgba(13,71,161,0.15)}`}</style>
    </div>
  );
}

// ============ Broadcasts ============
function BroadcastsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: '', message: '', audience: 'customers', channel: 'in_app' });
  const [busy, setBusy] = useState(false);

  const reload = () => api.get('/admin/broadcasts').then(r => setList(r.data));
  useEffect(() => { reload().catch(() => {}); }, []);

  const send = async () => {
    if (!form.title || !form.message) return toast.error('Title and message required');
    setBusy(true);
    try {
      const r = await api.post('/admin/broadcasts', form);
      toast.success(`Broadcast sent to ${r.data.recipient_count} ${form.audience}`);
      if (r.data.whatsapp_url) {
        window.open(r.data.whatsapp_url, '_blank');
      }
      setForm({ title: '', message: '', audience: 'customers', channel: 'in_app' });
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5 space-y-4">
      <div className="card-soft p-5">
        <div className="font-heading font-bold text-base mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#0D47A1]"/>New broadcast</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="bc-title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="Title (e.g. Diwali 10% off)" className="ainp"/>
          <select data-testid="bc-audience" value={form.audience} onChange={e=>setForm({...form, audience:e.target.value})} className="ainp">
            <option value="customers">All customers</option>
            <option value="owners">All shop owners</option>
            <option value="all">All users</option>
          </select>
          <div className="md:col-span-2"><textarea data-testid="bc-message" rows={4} value={form.message} onChange={e=>setForm({...form, message:e.target.value})} placeholder="Message body…" className="ainp"/></div>
          <select data-testid="bc-channel" value={form.channel} onChange={e=>setForm({...form, channel:e.target.value})} className="ainp">
            <option value="in_app">In-app notification (free)</option>
            <option value="whatsapp_link">In-app + WhatsApp share-link (free)</option>
          </select>
          <button data-testid="bc-send-btn" disabled={busy} onClick={send} className="rounded-full bg-[#0D47A1] hover:bg-[#0a3578] text-white px-5 py-2 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/>Sending…</> : <><Send className="w-4 h-4"/>Send broadcast</>}
          </button>
        </div>
      </div>

      <div className="card-soft p-4">
        <div className="font-heading font-bold text-base mb-2">Recent broadcasts</div>
        <div className="space-y-2 max-h-[400px] overflow-auto">
          {list.length === 0 && <div className="text-sm text-ink/40 py-4 text-center">No broadcasts yet.</div>}
          {list.map(b => (
            <div key={b.id} data-testid={`bc-row-${b.id}`} className="rounded-lg border border-border p-3 bg-white">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold">{b.title}</div>
                <span className="text-xs text-ink/60">{new Date(b.created_at).toLocaleString()} · {b.recipient_count} recipients · {b.channel}</span>
              </div>
              <div className="text-sm text-ink/80 mt-1">{b.message}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`.ainp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;outline:none}.ainp:focus{border-color:#0D47A1;box-shadow:0 0 0 3px rgba(13,71,161,0.15)}`}</style>
    </div>
  );
}

// ============ Financials ============
function FinancialsTab() {
  const [data, setData] = useState(null);
  const [gstr8, setGstr8] = useState(null);
  const [gstr8Month, setGstr8Month] = useState(() => new Date().toISOString().slice(0, 7));
  const [gstr8Loading, setGstr8Loading] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [caSettings, setCaSettings] = useState(null);
  const [caForm, setCaForm] = useState({ ca_email: '', ca_name: '' });
  const [caEditing, setCaEditing] = useState(false);

  useEffect(() => { api.get('/admin/financials').then(r => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get('/admin/settings/ca').then(r => {
      setCaSettings(r.data);
      setCaForm({ ca_email: r.data.ca_email || '', ca_name: r.data.ca_name || '' });
    }).catch(() => {});
  }, []);

  const loadGstr8 = async (m) => {
    setGstr8Loading(true);
    try {
      const r = await api.get('/admin/gstr8/summary', { params: { month: m } });
      setGstr8(r.data);
    } catch { toast.error('Could not load GSTR-8 summary'); }
    finally { setGstr8Loading(false); }
  };
  useEffect(() => { loadGstr8(gstr8Month); /* eslint-disable-next-line */ }, [gstr8Month]);

  const downloadAccountantPack = async () => {
    setPackBusy(true);
    try {
      const res = await api.get('/admin/accountant-pack.zip', {
        params: { month: gstr8Month },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BharatCart-Accountant-Pack-${gstr8Month}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Accountant pack for ${gstr8Month} downloaded — email it to your CA.`);
    } catch {
      toast.error('Pack download failed');
    } finally { setPackBusy(false); }
  };

  const saveCa = async () => {
    if (caForm.ca_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(caForm.ca_email)) {
      return toast.error('Enter a valid email address');
    }
    try {
      await api.put('/admin/settings/ca', caForm);
      const r = await api.get('/admin/settings/ca');
      setCaSettings(r.data);
      setCaEditing(false);
      toast.success('CA contact saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const emailPack = async () => {
    setEmailBusy(true);
    try {
      const r = await api.post('/admin/accountant-pack/email', { month: gstr8Month });
      toast.success(r.data.message || 'Pack emailed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed — set RESEND_API_KEY in backend/.env');
    } finally { setEmailBusy(false); }
  };

  if (!data) return <div className="mt-5 card-soft p-5 text-sm text-ink/60">Loading…</div>;

  const downloadLedger = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/financials/ledger.csv?days=30`, '_blank');
  const downloadGstr8 = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gstr8/export.csv?month=${gstr8Month}`, '_blank');

  const Stat = ({ label, value, sub }) => (
    <div className="card-soft p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/50">{label}</div>
      <div className="font-heading font-black text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs text-ink/60 mt-0.5">{sub}</div>}
    </div>
  );

  const emailReady = caSettings?.resend_configured && (caForm.ca_email || caSettings?.ca_email);

  return (
    <div className="mt-5 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="GMV 30d" value={`₹${data.gmv_30d.toLocaleString('en-IN', {maximumFractionDigits: 0})}`} sub={`${data.orders_30d} orders`}/>
        <Stat label="Refunds 30d" value={`₹${Math.abs(data.refunds_30d).toLocaleString('en-IN', {maximumFractionDigits: 0})}`}/>
        <Stat label="Platform revenue 30d" value={`₹${data.revenue_30d.toLocaleString('en-IN', {maximumFractionDigits: 0})}`} sub={`@ ${data.commission_pct}% commission`}/>
        <Stat label="Profit 30d" value={`₹${data.profit_30d.toLocaleString('en-IN', {maximumFractionDigits: 0})}`} sub={`Revenue − refunds − expenses`}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="GMV (1 year)" value={`₹${data.gmv_year.toLocaleString('en-IN', {maximumFractionDigits: 0})}`}/>
        <Stat label="GMV (lifetime)" value={`₹${data.gmv_lifetime.toLocaleString('en-IN', {maximumFractionDigits: 0})}`}/>
        <Stat label="Owner KYC status" value={`${data.kyc.verified} verified`} sub={`${data.kyc.submitted} submitted · ${data.kyc.pending} pending`}/>
      </div>

      {/* 📦 Accountant Pack — the "one big button" for CAs */}
      <div className="rounded-3xl border-2 border-[#1B5E20]/30 bg-gradient-to-br from-[#E8F5E9] to-[#F1F8E9] p-5 md:p-6" data-testid="accountant-pack-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-[#1B5E20] text-white grid place-items-center flex-shrink-0">
            <BookOpenCheck className="w-6 h-6"/>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#1B5E20]">Monthly accountant pack</div>
            <div className="font-heading font-black text-xl md:text-2xl text-ink mt-0.5">One ZIP · Everything your CA needs</div>
            <p className="text-sm text-ink/70 mt-2 max-w-2xl">
              Sales register (for GSTR-1 &amp; GSTR-3B), TCS register (GSTR-8), commission income &amp; payout ledger — <b>download</b> or <b>email straight to your CA</b> every month.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
              <span className="pill !bg-white !border-[#1B5E20]/30 !text-[#1B5E20]">01_sales_register.csv</span>
              <span className="pill !bg-white !border-[#1B5E20]/30 !text-[#1B5E20]">02_gstr8_tcs_register.csv</span>
              <span className="pill !bg-white !border-[#1B5E20]/30 !text-[#1B5E20]">03_commission_income.csv</span>
              <span className="pill !bg-white !border-[#1B5E20]/30 !text-[#1B5E20]">04_payout_register.csv</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 md:min-w-[240px]">
            <input
              data-testid="accountant-pack-month"
              type="month"
              value={gstr8Month}
              onChange={e => setGstr8Month(e.target.value)}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm"
            />
            <button
              data-testid="accountant-pack-download"
              disabled={packBusy}
              onClick={downloadAccountantPack}
              className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-5 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {packBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
              Download pack
            </button>
            <button
              data-testid="accountant-pack-email"
              disabled={emailBusy || !emailReady}
              onClick={emailPack}
              title={!caSettings?.resend_configured ? 'Set RESEND_API_KEY in backend/.env to enable email' : (!caForm.ca_email ? 'Save your CA email below first' : 'Send this pack to your CA')}
              className="rounded-full bg-[#0D47A1] hover:bg-[#0a3578] text-white px-5 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              Email to CA
            </button>
          </div>
        </div>

        {/* CA contact strip */}
        <div className="mt-5 pt-4 border-t border-[#1B5E20]/15">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 pt-2">Your CA</div>
            {!caEditing ? (
              <div className="flex-1 min-w-[200px] flex items-center gap-3 flex-wrap">
                {caSettings?.ca_email ? (
                  <>
                    <div className="text-sm">
                      <b className="text-ink">{caSettings.ca_name || 'CA'}</b>
                      <span className="text-ink/60"> · {caSettings.ca_email}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-ink/60 italic">No CA email saved yet</div>
                )}
                <button data-testid="ca-edit-btn" onClick={() => setCaEditing(true)} className="text-xs text-[#0D47A1] font-semibold hover:underline">
                  {caSettings?.ca_email ? 'Change' : 'Add now →'}
                </button>
                {!caSettings?.resend_configured && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider bg-[#FFF3E0] text-[#9A4500] px-2 py-1 rounded-full font-bold">
                    ⚠️ Email disabled — RESEND_API_KEY not set
                  </span>
                )}
              </div>
            ) : (
              <div className="flex-1 min-w-[200px] flex items-center gap-2 flex-wrap">
                <input
                  data-testid="ca-name-input"
                  placeholder="CA name (e.g., Amit Sharma)"
                  value={caForm.ca_name}
                  onChange={e => setCaForm({ ...caForm, ca_name: e.target.value })}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm flex-1 min-w-[160px]"
                />
                <input
                  data-testid="ca-email-input"
                  placeholder="ca@firm.in"
                  type="email"
                  value={caForm.ca_email}
                  onChange={e => setCaForm({ ...caForm, ca_email: e.target.value })}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-sm flex-1 min-w-[180px]"
                />
                <button data-testid="ca-save-btn" onClick={saveCa} className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-4 py-2 text-sm font-bold">Save</button>
                <button onClick={() => { setCaEditing(false); setCaForm({ ca_email: caSettings?.ca_email || '', ca_name: caSettings?.ca_name || '' }); }} className="text-xs text-ink/50 hover:underline">Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-soft p-5">
        <div className="font-heading font-bold text-base mb-2">Individual reports</div>
        <p className="text-sm text-ink/70 mb-3">Prefer just one CSV? Download the 30-day ledger — compatible with Tally, Zoho Books and most accounting software.</p>
        <button data-testid="fin-ledger-btn" onClick={downloadLedger} className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-2"><Download className="w-4 h-4"/>Download 30-day ledger CSV</button>
      </div>

      {/* GSTR-8 (TCS return) */}
      <div className="card-soft p-5" data-testid="gstr8-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1B5E20]">Statutory · Section 52 CGST</div>
            <div className="font-heading font-bold text-lg mt-0.5">GSTR-8 — TCS return</div>
            <p className="text-xs text-ink/60 mt-1">
              As an e-commerce operator, BharatCart must file GSTR-8 by the <b>10th of each month</b>. This report groups TCS (1%) collected per seller for the selected month.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="gstr8-export-btn" onClick={downloadGstr8} className="rounded-full bg-[#1B5E20] hover:bg-[#0D3D14] text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-2">
              <Download className="w-4 h-4"/>Export CSV
            </button>
          </div>
        </div>

        {gstr8Loading ? (
          <div className="mt-4 text-sm text-ink/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Aggregating…</div>
        ) : !gstr8 ? null : (
          <>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              <div className="rounded-lg bg-[#E8F5E9] p-3">
                <div className="opacity-70">Orders</div>
                <div className="font-bold text-lg text-[#1B5E20]">{gstr8.totals.orders}</div>
              </div>
              <div className="rounded-lg bg-[#E8F5E9] p-3">
                <div className="opacity-70">Gross supplies</div>
                <div className="font-bold text-lg text-[#1B5E20]">₹{gstr8.totals.gross_supplies.toLocaleString('en-IN', {maximumFractionDigits: 2})}</div>
              </div>
              <div className="rounded-lg bg-[#FBE9E7] p-3">
                <div className="opacity-70">Returned</div>
                <div className="font-bold text-lg text-[#BF360C]">₹{gstr8.totals.returned_supplies.toLocaleString('en-IN', {maximumFractionDigits: 2})}</div>
              </div>
              <div className="rounded-lg bg-[#E3F2FD] p-3">
                <div className="opacity-70">Net supplies</div>
                <div className="font-bold text-lg text-[#0D47A1]">₹{gstr8.totals.net_supplies.toLocaleString('en-IN', {maximumFractionDigits: 2})}</div>
              </div>
              <div className="rounded-lg bg-[#FFF3E0] p-3">
                <div className="opacity-70">TCS collected</div>
                <div className="font-bold text-lg text-[#9A4500]">₹{gstr8.totals.tcs_total.toLocaleString('en-IN', {maximumFractionDigits: 2})}</div>
              </div>
            </div>

            <div className="mt-3 text-[11px] text-ink/60">
              <b>Filing entity:</b> {gstr8.platform.legal_name} · GSTIN <span className="font-mono">{gstr8.platform.gstin}</span> · PAN <span className="font-mono">{gstr8.platform.pan}</span>
            </div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink/50 border-b border-border bg-[#FBF8F1]">
                    <th className="py-2 px-3">Seller GSTIN</th>
                    <th className="py-2 px-3">Seller</th>
                    <th className="py-2 px-3 text-right">Orders</th>
                    <th className="py-2 px-3 text-right">Net supplies</th>
                    <th className="py-2 px-3 text-right">TCS CGST</th>
                    <th className="py-2 px-3 text-right">TCS SGST</th>
                    <th className="py-2 px-3 text-right">TCS total</th>
                  </tr>
                </thead>
                <tbody>
                  {gstr8.rows.length === 0 ? (
                    <tr><td colSpan={7} className="py-6 text-center text-ink/40">No supplies in {gstr8.month}</td></tr>
                  ) : gstr8.rows.map(r => (
                    <tr key={r.gstin} data-testid={`gstr8-row-${r.gstin}`} className="border-b border-border/40">
                      <td className="py-2 px-3 font-mono text-[11px]">{r.gstin}</td>
                      <td className="py-2 px-3">{r.seller_name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{r.orders}</td>
                      <td className="py-2 px-3 text-right tabular-nums">₹{r.net_supplies.toLocaleString('en-IN', {maximumFractionDigits: 2})}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-[#9A4500]">₹{r.tcs_cgst.toLocaleString('en-IN', {maximumFractionDigits: 2})}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-[#9A4500]">₹{r.tcs_sgst.toLocaleString('en-IN', {maximumFractionDigits: 2})}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold text-[#9A4500]">₹{r.tcs_total.toLocaleString('en-IN', {maximumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-[11px] text-[#9A4500] bg-[#FFF3E0] rounded-lg p-2">
              📌 {gstr8.notice}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============ KYC verify ============
function KycVerifyTab() {
  const [owners, setOwners] = useState([]);

  const reload = async () => {
    // We don't have a dedicated owners list endpoint; piggy-back on customers list with role filter
    const r = await api.get('/admin/customers', { params: {} });
    // The list endpoint filters to customers — we need to call differently. We'll show financials' KYC count for now and prompt to use shell.
    setOwners([]);
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="mt-5 card-soft p-5 space-y-3">
      <div className="font-heading font-bold text-base flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#1B5E20]"/>Owner KYC verification</div>
      <p className="text-sm text-ink/70">After an owner submits KYC + valid GSTIN, you (admin) flip them to <b>verified</b> here so the <b>Verified Dukandaar 🏅</b> badge appears on their store.</p>
      <p className="text-xs text-ink/50">POST endpoint: <code>/api/admin/owners/&#123;owner_id&#125;/verify-kyc?status=verified</code> — UI list coming next iteration. Use the Admin chat ("Show pending KYC owners") or run the API directly via terminal for now.</p>
    </div>
  );
}

// ============ Tickets ============
function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState('');

  const reload = () => api.get('/admin/tickets').then(r => setTickets(r.data));
  useEffect(() => { reload().catch(() => {}); }, []);

  const sendReply = async () => {
    if (!active || !reply.trim()) return;
    await api.post(`/admin/tickets/${active.id}/reply`, { message: reply });
    setReply('');
    await reload();
    toast.success('Reply sent');
  };

  return (
    <div className="mt-5 grid lg:grid-cols-[1fr_1.5fr] gap-4">
      <div className="card-soft p-4 max-h-[500px] overflow-auto">
        <div className="font-heading font-bold text-base mb-2">Open tickets ({tickets.length})</div>
        {tickets.length === 0 && <div className="text-sm text-ink/40 py-4">No support tickets yet.</div>}
        {tickets.map(t => (
          <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => setActive(t)} className={`w-full text-left rounded-lg border p-3 mb-2 ${active?.id === t.id ? 'border-[#0D47A1] bg-[#E3F2FD]' : 'border-border hover:border-[#0D47A1]/50'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-sm">{t.subject}</div>
              <span className={`text-[10px] rounded-full px-2 py-0.5 ${t.status === 'open' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{t.status}</span>
            </div>
            <div className="text-xs text-ink/60 mt-0.5">{t.user_name || t.user_phone} · {new Date(t.created_at).toLocaleString()}</div>
          </button>
        ))}
      </div>
      <div className="card-soft p-4 mandispot-printable">
        {active ? (
          <>
            <PrintShell
              kind="Support Ticket"
              docId={`#${active.id.slice(0, 8).toUpperCase()}`}
              dateText={new Date(active.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            />
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <div className="font-heading font-bold text-base mb-1">{active.subject}</div>
                <div className="text-xs text-ink/60 mb-3">{active.user_name || active.user_phone} ({active.user_role})</div>
              </div>
              <PrintButton label="Print ticket" testId="print-ticket-btn"/>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-auto mb-3 rounded-lg bg-[#FBF8F1] p-3">
              {(active.messages || []).map((m, i) => (
                <div key={i} className={`text-sm ${m.role === 'admin' ? 'text-[#0D47A1] font-semibold' : 'text-ink/80'}`}>
                  <span className="text-[10px] uppercase tracking-wider mr-2 opacity-60">{m.role}</span>{m.text}
                </div>
              ))}
            </div>
            <textarea data-testid="ticket-reply" rows={3} value={reply} onChange={e=>setReply(e.target.value)} placeholder="Type your reply…" className="ainp"/>
            <button data-testid="ticket-send-reply" onClick={sendReply} className="mt-2 rounded-full bg-[#0D47A1] hover:bg-[#0a3578] text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-2"><Send className="w-4 h-4"/>Send reply</button>
          </>
        ) : <div className="text-sm text-ink/40 py-12 text-center">Pick a ticket on the left to read & reply.</div>}
      </div>
      <style>{`.ainp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;outline:none}.ainp:focus{border-color:#0D47A1;box-shadow:0 0 0 3px rgba(13,71,161,0.15)}`}</style>
    </div>
  );
}

// ============ Admin Master Catalog ============
function AdminCatalogTab() {
  const [items, setItems] = useState(null);
  const load = async () => {
    const r = await api.get('/master-catalog');
    setItems(r.data.items);
  };
  useEffect(() => { load(); }, []);

  const overrideImage = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const up = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = up.data.url || up.data.file_url || up.data.path;
      await api.patch(`/admin/master-catalog/${id}`, { image_url: url });
      toast.success('Real photo applied platform-wide!');
      await load();
    } catch (e) { toast.error('Upload failed'); }
  };

  if (!items) return <div className="mt-5 card-soft p-5 text-sm text-ink/60">Loading…</div>;

  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-2xl border-2 border-[#0D47A1]/20 bg-gradient-to-br from-[#E3F2FD] to-white p-5" data-testid="admin-catalog-hero">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#0D47A1] text-white grid place-items-center"><Sparkles className="w-5 h-5"/></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0D47A1]">Master Catalog Override</div>
            <div className="font-heading font-black text-xl md:text-2xl">Replace SVG icons with real photos</div>
            <p className="text-sm text-ink/70 mt-1">Upload a professional product photo for any category. It auto-applies to every shop across BharatCart.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {items.map(it => (
          <div key={it.id} data-testid={`admin-cat-${it.slug}`} className="rounded-2xl border border-border bg-white p-4 flex flex-col text-center">
            <div className="w-16 h-16 rounded-full mx-auto grid place-items-center overflow-hidden mb-2" style={{ background: it.tint_bg, color: it.tint_fg }}>
              {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover"/> : <span className="w-9 h-9 block" dangerouslySetInnerHTML={{ __html: it.icon_svg }}/>}
            </div>
            <div className="font-heading font-bold text-sm">{it.name}</div>
            <div className="text-[10px] text-ink/50 uppercase tracking-wider">{it.category_group}</div>
            <label className="mt-2 rounded-full bg-[#0D47A1] hover:bg-[#0a3578] text-white py-1.5 px-3 text-xs font-bold inline-flex items-center justify-center gap-1 cursor-pointer">
              <Upload className="w-3 h-3"/>{it.image_url ? 'Replace' : 'Upload photo'}
              <input data-testid={`admin-cat-upload-${it.slug}`} type="file" accept="image/*" hidden onChange={e => overrideImage(it.id, e.target.files?.[0])}/>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
